import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { router } from 'expo-router';

import type { CitationRequest } from '../model/citationRequest';
import type {
  AttachedStatementReference,
  ExtractedStatement,
  StatementCitationStatus,
  StatementReferenceHelpRequest,
} from '../model/extractedStatements';
import type { StatementTriageOpenSource } from '../model/statementTriage';
import { openCitationRequestReview } from '../state/citationRequestReviewStore';
import { setCitationWizardHandoff } from '../state/citationWizardHandoffStore';
import { getStatementTriageStore } from '../state/statementTriageStore';

interface OpenCitationReviewWizardInput {
  conversationId: string;
  /** Twilio SID of the parent expert message — the per-message store
   * registry key and the native route's `expectedMessageSid` guard. */
  messageSid: string;
  extractedStatements: readonly ExtractedStatement[];
  statementCitationStatuses: readonly StatementCitationStatus[] | undefined;
  attachedStatementReferences: readonly AttachedStatementReference[];
  statementReferenceHelpRequests?: readonly StatementReferenceHelpRequest[];
  /**
   * Where the open was triggered from, recorded on the OPENED analytics event.
   * Defaults to `inline_prompt` (the in-chat card), so existing callers keep
   * their attribution without change.
   */
  openSource?: StatementTriageOpenSource;
  /**
   * Active citation request for the message, when known. Used to seed
   * the per-request review store with the message's already-attached
   * refs so the review step's checkboxes reflect what's currently
   * published. The seed is a no-op unless the request is reviewable
   * (`awaiting_review` / `published`).
   */
  citationRequest?: CitationRequest;
}

/**
 * Open the reference-request wizard for a message, picking the right
 * surface per platform. Shared by the `AddReferencesPromptBubble` pill
 * tap and the `referencesReady` deep-link auto-open
 * (`useAutoOpenReferencesReady`) so both honour the same web-vs-native
 * split.
 *
 * - **Web**: flips the per-message triage store's `isOpen` flag. The
 *   inline `StatementTriageSheet.web.tsx` mounted in the bubble reacts
 *   and renders the modal; its sync effect lands it on the right phase
 *   (`review` for `awaiting_review` / `published`).
 * - **Native**: the inline sheet is dead (see `StatementTriageSheet.tsx`).
 *   Instead we hand the heavy inputs to `citationWizardHandoffStore`
 *   **synchronously** and push the `/(secure)/inbox/chat/citation-wizard`
 *   full-screen route, which reads the payload on mount. Bails when
 *   there are no extracted statements — the route renders nothing
 *   useful without them.
 *
 * Returns `true` when a surface was opened, `false` when the native
 * path bailed because there were no extracted statements (lets the
 * deep-link caller leave its retry guard unset so a later cache update
 * can re-attempt).
 */
export function openCitationReviewWizard({
  conversationId,
  messageSid,
  extractedStatements,
  statementCitationStatuses,
  attachedStatementReferences,
  statementReferenceHelpRequests,
  citationRequest,
  openSource = 'inline_prompt',
}: OpenCitationReviewWizardInput): boolean {
  // Native bails before any store mutation: the route renders nothing
  // without extracted statements, so seeding the review store here would
  // leave it primed for a session that never launches.
  if (!isWeb && extractedStatements.length === 0) {
    return false;
  }

  if (
    citationRequest &&
    (citationRequest.status === 'awaiting_review' || citationRequest.status === 'published')
  ) {
    openCitationRequestReview(citationRequest, attachedStatementReferences);
  }

  if (!isWeb) {
    setCitationWizardHandoff({
      conversationId,
      messageSid,
      extractedStatements: [...extractedStatements],
      statementCitationStatuses,
      attachedStatementReferences,
      statementReferenceHelpRequests,
      openSource,
    });

    router.push({
      pathname: '/(secure)/inbox/chat/citation-wizard',
      params: { expectedMessageSid: messageSid },
    });

    return true;
  }

  const store = getStatementTriageStore(messageSid);

  // The search/review entry shares the per-message triage store with the
  // provide entry. If the expert last used "I have references in mind",
  // the store still holds `entryMode: 'provide'` and `phase: 'provide'`
  // (close() preserves both). Reopening for search/review without
  // clearing that would render the stale provide phase — and the sheet's
  // citation-request sync effect early-returns in provide mode, so the
  // review reconciliation would never run. Reset back to a clean search
  // entry first; the sync effect then drives the correct
  // searching/review phase from the active citation request.
  if (store.getState().entryMode !== 'search') {
    store.getState().reset();
  }

  store.getState().setOpenSource(openSource);

  store.getState().open();

  return true;
}
